import {
  OptionGroup,
  SelectedExperienceType,
} from '@common/features/DynamicPackagesConfigurationWizard/types';
import { getDatesRange } from '@utils/helpers';
import { some } from '@utils/some';
import { Moment } from 'moment';
import { useMemo } from 'react';

export const useGroupDiningOptionsByDateAndOptionGroup = <T extends { dayNumber?: number }>(
  startDate: Moment,
  endDate: Moment,
  options: T[]
) => {
  const stayDuration = endDate
    .add(1, 'd')
    .diff(startDate.startOf('d').format('YYYY-MM-DD'), 'days');
  const dates = getDatesRange(startDate, stayDuration);
  const groupedOptions = useMemo(() => {
    const mappedDiningOptions = options.reduce(
      (
        groupedOptions: Array<{
          options: T[];
          date: string;
        }>,
        option
      ) => {
        const day = option.dayNumber ?? 0;
        groupedOptions[day ?? 0]?.options.push(option);
        return groupedOptions;
      },
      dates.map(({ date }) => ({
        options: [],
        date: date.format('DD MMM yyyy'),
      }))
    );
    return mappedDiningOptions;
  }, [dates, options]);

  return groupedOptions;
};

export const useGroupOptionsByDateAndOptionGroup = (
  startDate: Moment,
  selectedExperiences: SelectedExperienceType[],
  isPackageRepeating: boolean,
  stayDuration: number,
  numberOfGuests: number
) => {
  const dates = getDatesRange(startDate, stayDuration + 1);

  if (isPackageRepeating) {
    const datesWithOptions = dates.map(({ date }) => ({
      date: date.format('DD MMM yyyy'),
      options: [],
    }));

    selectedExperiences.forEach((opt) => {
      const dayIndex = opt.dayNumber ?? 0;
      const isIncluded = opt.isIncluded ?? false;
      const optionGroupId = Number(opt.optionGroupId);
      const isChosenByAllGuests = opt.count === numberOfGuests;

      const date = datesWithOptions[dayIndex];
      if (!date) return;

      const existingOptions = date.options.find(
        (o) =>
          o?.optionGroupId === optionGroupId && o.isIncluded === (isIncluded || isChosenByAllGuests)
      );

      if (existingOptions) {
        existingOptions.options.push(opt);
      } else {
        date.options.push({
          dayNumber: dayIndex,
          isIncluded: isIncluded || isChosenByAllGuests,
          optionGroupId,
          options: [opt],
        });
      }
    });

    return datesWithOptions.filter((day) => some(day.options));
  }

  const selectedExperiencesByOptionGroup: { [key: string]: SelectedExperienceType[] } =
    selectedExperiences.reduce((groups, experience) => {
      if (experience.isIncluded) {
        groups[experience.optionId] = [experience];
        return groups;
      }

      if (!experience.optionGroupId) {
        return groups;
      }

      groups[experience.optionGroupId] = groups[experience.optionGroupId] || [];
      groups[experience.optionGroupId].push(experience);
      return groups;
    }, {} as { [key: string]: SelectedExperienceType[] });

  const optionGroups = Object.values(selectedExperiencesByOptionGroup).reduce(
    (
      acc: {
        options: SelectedExperienceType[];
        isIncluded: boolean;
        dayNumber: number;
        optionGroupId: number;
      }[],
      group: SelectedExperienceType[]
    ) => {
      const experiencesChosenByAllGuests =
        group.filter((item) => item.count === numberOfGuests) || [];
      const otherExperiences = group.filter((item) => item.count !== numberOfGuests);
      experiencesChosenByAllGuests.forEach((exp) => {
        acc.push({
          options: [exp],
          isIncluded: true,
          dayNumber: group[0].dayNumber,
          optionGroupId: Number(group[0].optionGroupId),
        });
      });
      if (some(otherExperiences)) {
        acc.push({
          options: otherExperiences,
          isIncluded: group.every((item) => item.isIncluded === true),
          dayNumber: group[0].dayNumber,
          optionGroupId: Number(group[0].optionGroupId),
        });
      }
      return acc;
    },
    []
  );

  const groupedOptions = useMemo(() => {
    const maxDayNumber = optionGroups.reduce(
      (max, option) => Math.max(max, option.dayNumber ?? 0),
      0
    );
    const requiredLength = Math.max(dates.length, maxDayNumber + 1);

    const initialArray = Array.from({ length: requiredLength }, (_, index) => {
      const dateForDay = startDate.clone().add(index, 'days');
      return {
        options: [] as { options: OptionGroup[]; isIncluded: boolean; optionGroupId: number }[],
        date: dateForDay.format('DD MMM yyyy'),
      };
    });

    const mappedOptions = optionGroups.reduce((groupedOptions, option) => {
      const day = option.dayNumber ?? 0;
      groupedOptions[day]?.options.push(option);
      return groupedOptions;
    }, initialArray);

    return mappedOptions.filter((day) => some(day.options));
  }, [dates, optionGroups, startDate]);
  return groupedOptions;
};
